import { Stack, Text } from "@certn/cascadia";
import { InferClientPortalAPI } from "@certn/api";
import { LinkButton, SummaryMessage } from "@certn/cascadia-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type BillingNoticesData = NonNullable<
  InferClientPortalAPI<"/api/internal/client-portal/customer-accounts/billing-notices/">
>;
type BlockingProfile = BillingNoticesData["blocking_profiles"][number];

interface Props {
  profile: BlockingProfile;
}

/**
 * DAT-1730: Inline message rendered on the order review step when the user's
 * resolved billing profile is in the "blocking" state (DAILY + CLIENT-pay +
 * unpaid invoices + LD flag on, or a DAT-2106 collections hold). Paired with
 * `disableCompletion={true}` on the surrounding `OrderStepLayout` so the
 * Submit button is greyed out — the backend 402 in `submit_order` is still
 * the authoritative gate.
 */
export function OrderBlockedMessage({ profile }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCollectionsHold = profile.reason === "COLLECTIONS";

  return (
    <SummaryMessage type="danger">
      <div>
        <Stack gap="space-2" orientation="horizontal">
          <Text color="text-base" style="global/heading-small">
            {t("Ordering is paused for this billing profile")}
          </Text>
        </Stack>
        <Stack gap="space-2" orientation="horizontal">
          <Text color="text-weak" style="global/body-secondary">
            {isCollectionsHold && profile.total_outstanding_amount === null
              ? t(
                  "Your service has been paused for {{profileName}} due to non-payment. Submit payment for the full overdue balance to restore ordering access.",
                  { profileName: profile.billing_profile_name }
                )
              : isCollectionsHold
                ? t(
                    "Your service has been paused for {{profileName}} due to non-payment ({{amount}} {{currency}} overdue). Submit payment for the full overdue balance to restore ordering access.",
                    {
                      profileName: profile.billing_profile_name,
                      amount: profile.total_outstanding_amount,
                      currency: profile.currency,
                    }
                  )
                : t(
                    "There is currently {{count}} unpaid invoice(s) for {{profileName}} totaling {{amount}} {{currency}}. Please pay the balance to resume ordering immediately.",
                    {
                      profileName: profile.billing_profile_name,
                      count: profile.unpaid_invoice_count,
                      amount: profile.total_outstanding_amount,
                      currency: profile.currency,
                    }
                  )}
          </Text>
        </Stack>
        <Stack gap="space-2" orientation="horizontal">
          <LinkButton
            linkStyle="monochrome"
            textStyle="global/body-secondary"
            onPress={() => navigate("/billing/invoices/current?status=OPEN")}
            testID="order-blocked-banner-link"
          >
            {t("View invoices")}
          </LinkButton>
        </Stack>
      </div>
    </SummaryMessage>
  );
}
